(() => {
  const cardSelector = '[data-vintoop-lot-preview-card], [data-vintoop-collection-lot-preview-card]';
  const soldOutCardSelector = '.vintoop-video-lot-card--sold-out, .vintoop-latest-lot-card--sold-out';
  const previewState = new WeakMap();

  function getState(card) {
    if (!previewState.has(card)) {
      previewState.set(card, { previewRequested: false });
    }

    return previewState.get(card);
  }

  function getPreviewStartTime(video) {
    const configuredStart = Number.parseFloat(video.dataset.previewStartTime);
    const startTime = Number.isFinite(configuredStart) && configuredStart >= 0 ? configuredStart : 0;
    const duration = Number(video.duration);

    if (!Number.isFinite(duration) || duration <= 0) return startTime;

    return Math.min(startTime, Math.max(0, duration - 0.05));
  }

  function attachPreviewSource(video) {
    if (video.querySelector('source')) return true;

    const url = video.dataset.previewUrl;
    if (!url) return false;

    const source = document.createElement('source');
    source.src = url;
    source.type = video.dataset.previewType || 'video/mp4';
    video.append(source);
    video.load();
    return true;
  }

  function unloadPreview(card) {
    const video = card.querySelector('[data-vintoop-lot-preview-video]');
    const button = card.querySelector('[data-vintoop-lot-preview-toggle]');
    if (!video) return;

    getState(card).previewRequested = false;
    video.pause();
    video.replaceChildren();
    video.removeAttribute('src');
    video.load();
    card.classList.remove('is-previewing');
    if (button) button.setAttribute('aria-pressed', 'false');
  }

  async function playLoadedPreview(card, video) {
    const state = getState(card);
    const button = card.querySelector('[data-vintoop-lot-preview-toggle]');
    if (!state.previewRequested) return;

    try {
      await video.play();
      if (!state.previewRequested) {
        video.pause();
        return;
      }

      card.classList.add('is-previewing');
      if (button) button.setAttribute('aria-pressed', 'true');
    } catch (error) {
      unloadPreview(card);
    }
  }

  function seekAndPlay(card, video) {
    if (!getState(card).previewRequested) return;

    const startTime = getPreviewStartTime(video);
    if (Math.abs(video.currentTime - startTime) < 0.03) {
      playLoadedPreview(card, video);
      return;
    }

    video.addEventListener('seeked', () => playLoadedPreview(card, video), { once: true });
    video.currentTime = startTime;
  }

  function startPreview(card) {
    const video = card.querySelector('[data-vintoop-lot-preview-video]');
    if (!video) return;

    const state = getState(card);
    state.previewRequested = true;
    if (!attachPreviewSource(video)) {
      state.previewRequested = false;
      return;
    }

    if (video.readyState < 1) {
      video.addEventListener('loadedmetadata', () => seekAndPlay(card, video), { once: true });
      return;
    }

    seekAndPlay(card, video);
  }

  function initCard(card, observer, finePointer) {
    const video = card.querySelector('[data-vintoop-lot-preview-video]');
    const button = card.querySelector('[data-vintoop-lot-preview-toggle]');
    if (!video || !button) return;

    video.addEventListener('error', () => unloadPreview(card));
    observer.observe(card);

    card.addEventListener('mouseenter', () => {
      if (finePointer.matches) startPreview(card);
    });
    card.addEventListener('mouseleave', () => {
      if (finePointer.matches) unloadPreview(card);
    });
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (card.classList.contains('is-previewing')) {
        unloadPreview(card);
      } else {
        startPreview(card);
      }
    });
  }

  function initSoldOutCardClickthrough(root = document) {
    root.querySelectorAll(soldOutCardSelector).forEach((card) => {
      if (card.dataset.vintoopSoldOutClickthroughReady === 'true') return;
      card.dataset.vintoopSoldOutClickthroughReady = 'true';

      card.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.target.closest('a, button, input, select, textarea, video')) return;

        const destination = card.querySelector('a[href]');
        if (destination) window.location.assign(destination.href);
      });
    });
  }

  function initAll(root = document) {
    const cards = [...root.querySelectorAll(cardSelector)];
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) unloadPreview(entry.target);
        });
      },
      { threshold: 0.15 }
    );

    cards.forEach((card) => {
      if (card.dataset.vintoopPreviewReady === 'true') return;
      card.dataset.vintoopPreviewReady = 'true';
      initCard(card, observer, finePointer);
    });

    initSoldOutCardClickthrough(root);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) cards.forEach(unloadPreview);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
